import { isEditPage, isRelistPage, isEditOrRelistPage, routerParas, isAgentPage } from '../../helpers/router.helper';
import { ChangeDetectorRef, Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import {
    CategoryEnum,
    NEW_HOMES_CATEGORIES,
    UserDetails
} from '@classifieds-sell/datalayer';
import {
    DraftFacade,
    DraftState,
    RouterFacade,
    UserFacade
} from '@classifieds-sell/state';
import { addTrailingSlash, StepModel } from '@classifieds-sell/shared';
import { environment } from 'apps/property-sell/src/environments/environment';
import { combineLatest, from, Subject } from 'rxjs';
import { filter, first, skip, takeUntil } from 'rxjs/operators';
import { PROPERTY_DEFAULT_STEP_STRUCTURE } from '../../constants/property-step';
import {
    convertDateWithoutTime,
    getDifferenceHours
} from '../../helpers/date.helper';
import {
    isStepEnabled,
    updatePropertySteps
} from '../../helpers/property-step.helper';
import { StepIds } from '../../models/property-step/property-step-model';

@Component({
    selector: 'property-landing',
    templateUrl: './property-landing.component.html',
    styleUrls: ['./property-landing.component.scss']
})
export class PropertyLandingComponent implements OnInit, OnDestroy {
    private _destroyed$: Subject<void> = new Subject<void>();

    private _userLoaded$: Subject<void> = new Subject<void>();
    private _draftLoaded$: Subject<void> = new Subject<void>();

    private _init = false;

    // true if initial url was '.../4233/House'
    // false (default) if initial url was '.../4233/House/<stepId>'
    private _redirectedFromRoute = false;
    // true if there is invalid step or can be landed on currentStepId
    private _redirectedFromLanding = false;

    private _draftState: DraftState;
    private _userDetails: UserDetails;

    private _categoriesForAgentUser = [...NEW_HOMES_CATEGORIES, CategoryEnum.sectionsForSale, CategoryEnum.lifeStyle, CategoryEnum.rural];

    allSet = false;

    constructor(
        private _activatedRoute: ActivatedRoute,
        private _router: Router,
        private _changeDetectorRef: ChangeDetectorRef,
        private _userFacade: UserFacade,
        private _draftFacade: DraftFacade,
        private _routerFacade: RouterFacade
    ) {
        this.initialise();
    }

    ngOnDestroy(): void {
        this._destroyed$.next();
    }

    ngOnInit(): void {
        if (this.isWildcardPath()) {
            this.allSet = true;
            this._changeDetectorRef.markForCheck();
        }

        if (!this._init && !this.isWildcardPath()) {
            this._init = true;

            this._userFacade.init();

            this._draftFacade.init(
                this.getCategoryId(),
                this.getSubCategory(),
                this.getListingId()
            );
        }

        this._router.events
            .pipe(filter((e) => e instanceof NavigationEnd))
            .subscribe({
                next: () => {
                    if (this._redirectedFromLanding) {
                        this.allSet = true;
                        this._changeDetectorRef.markForCheck();
                    }
                }
            });

        from(this._userFacade.userDetails$)
            .pipe(takeUntil(this._destroyed$), skip(1), first())
            .subscribe((ud) => {
                this._userDetails = ud;
                this._userLoaded$.next();
            });

        from(this._draftFacade.state$)
            .pipe(takeUntil(this._destroyed$), skip(1), first())
            .subscribe((ds) => {
                if (!ds.isLoadingDraftSuccess) {
                    window.location.href = document.baseURI;
                    return;
                } else if (isEditOrRelistPage(this._activatedRoute, true)) {
                    this.validateListing(ds);
                }

                this._draftState = ds;
                this._draftLoaded$.next();
            });

        combineLatest([this._userLoaded$, this._draftLoaded$])
            .pipe(takeUntil(this._destroyed$), first())
            .subscribe(() => {
                this.prepping();
                this._draftFacade.resetPackageCodeIfNeeded({
                    isRelist: isRelistPage(this._activatedRoute, true),
                    isAgent: isAgentPage(this._activatedRoute, true)
                });
            });
    }

    // check whether ot not listing can relist or edit
    private validateListing(ds: DraftState): void {
        if (ds.metadata.isWithdrawn) {
            // only for relist
            if (isEditPage(this._activatedRoute, true)) {
                window.location.href = document.baseURI;
                return;
            }

            const startDate = new Date(ds.metadata.listingStartDate);
            const endDate = new Date(ds.metadata.listingEndDate);
            const differenceHours = getDifferenceHours(startDate, endDate);
            // if(start date + 4 hours less than or equal endDate then prevent load page)
            if (differenceHours <= 4) {
                window.location.href = document.baseURI;
                return;
            }
        } else {
            const todayWithoutTime = convertDateWithoutTime(new Date());
            const endDateWithoutTime = convertDateWithoutTime(
                new Date(ds.metadata.listingEndDate)
            );
            const endDateAdd45DaysWithoutTime = convertDateWithoutTime(
                new Date(ds.metadata.listingEndDate)
            );
            endDateAdd45DaysWithoutTime.setDate(
                endDateAdd45DaysWithoutTime.getDate() + 45
            );
            const isAllowLoadEditPage =
                isEditPage(this._activatedRoute, true) &&
                todayWithoutTime.getTime() < endDateWithoutTime.getTime();
            const isAllowLoadRelistPage =
                isRelistPage(this._activatedRoute, true) &&
                endDateWithoutTime.getTime() <= todayWithoutTime.getTime() &&
                todayWithoutTime.getTime() <=
                endDateAdd45DaysWithoutTime.getTime();
            if (!(isAllowLoadEditPage || isAllowLoadRelistPage)) {
                window.location.href = document.baseURI;
                return;
            }
        }
    }

    private initialise() {
        // this is needed to subscribe the event of initial navigation
        // which occurs by entering the url or redirected from somewhere
        this._router.events
            .pipe(
                filter((e) => e instanceof NavigationEnd),
                first()
            )
            .subscribe({
                next: (e) => {
                    const event = <NavigationEnd>e;
                    if (event.url !== event.urlAfterRedirects) {
                        this._redirectedFromRoute = true;
                    }
                }
            });
    }

    private getCategoryId(): CategoryEnum {
        return this._activatedRoute.firstChild.snapshot.data.categoryId;
    }

    private getSubCategory(): string {
        return this._activatedRoute.firstChild.snapshot.paramMap.get(
            'subCategory'
        );
    }

    private getListingId(): number | null {
        return routerParas(this._activatedRoute, true).listingId;
    }

    private getChildSegements(): string[] {
        const segments: string[] = [];
        let route = this._activatedRoute;
        while (route.firstChild) {
            route.firstChild.snapshot.url.forEach((u) => segments.push(u.path));
            route = route.firstChild;
        }

        return segments;
    }

    private getCurrentStepId(): StepIds {
        const segments = this.getChildSegements();
        return <StepIds>segments[segments.length - 1];
    }

    private isWildcardPath(): boolean {
        // a hacky solution, might want to look into different solution
        // because we don't want to redirect to 404 in route configuration
        // also want to keep the original url which happens to be 404
        return this._activatedRoute.firstChild.routeConfig.path === '**';
    }

    private prepping() {
        const category = this.getCategoryId();
        if (
            this._userDetails.memberSummary.isJobAgent ||
            this.shouldRedirectCategoryTo404(category)
        ) {
            window.location.href = `${addTrailingSlash(
                environment.trademeUrl
            )}404`;
            return;
        }

        if (category === CategoryEnum.residentialForSale) {
            const segments = this.getChildSegements();
            const url = `${addTrailingSlash(
                environment.frontendUrl
            )}${this.getCategoryId()}/${this.getSubCategory()}`;

            // Redirect Agents who enter the Direct Flow to the Agent Flow & vice versa
            if (
                this._userDetails.memberSummary.isPropertyAgent &&
                !segments.includes('Agent')
            ) {
                window.location.href = `${url}/Agent`;
                return;
            } else if (
                !this._userDetails.memberSummary.isPropertyAgent &&
                segments.includes('Agent')
            ) {
                window.location.href = `${url}`;
                return;
            }
        }

        const propertySteps = updatePropertySteps(
            PROPERTY_DEFAULT_STEP_STRUCTURE,
            this._draftState.validationResults
        );
        this._routerFacade.updateSteps(propertySteps);
        this.redirectIfRequired(propertySteps);
    }

    private shouldRedirectCategoryTo404(category: CategoryEnum): boolean {
        let shouldRedirect = false;

        // R4R has no agents
        if (
            category === CategoryEnum.residentialForRent &&
            this._userDetails.memberSummary.isPropertyAgent
        ) {
            shouldRedirect = true;
        }

        if (this._categoriesForAgentUser.some(x => x === category) &&
            !this._userDetails.memberSummary.isPropertyAgent) {
            shouldRedirect = true;
        }

        return shouldRedirect;
    }

    private redirectIfRequired(propertySteps: StepModel[]) {
        const stepIdToBeLandedOn = this.findStepId(propertySteps);

        if (stepIdToBeLandedOn !== this.getCurrentStepId()) {
            const segments = this.getChildSegements();
            segments.pop();
            segments.push(stepIdToBeLandedOn);

            setTimeout(() => {
                this._redirectedFromLanding = true;
                this._router.navigate(segments, {
                    relativeTo: this._activatedRoute,
                    queryParams: this._activatedRoute.snapshot.queryParams
                });
            }, 0);
            return;
        }

        this.allSet = true;
        this._changeDetectorRef.markForCheck();
    }

    private findStepId(propertySteps: StepModel[]): string {
        if (this._draftState.validationResults === null) {
            return StepIds.propertyInformation;
        }

        // find first invalid step if it exists - overrides currentStepId in state
        const firstInvalidIndex = this._draftState.validationResults.findIndex(
            (v) => v.isValid === false
        );

        const currentStepId = this.getCurrentStepId();

        if (firstInvalidIndex !== -1) {
            return this._draftState.validationResults[firstInvalidIndex].stepId;
        } else {
            if (this._redirectedFromRoute) {
                return (
                    this._draftState.draftData.currentStepId ||
                    StepIds.propertyInformation
                );
            } else {
                if (!isStepEnabled(propertySteps, currentStepId)) {
                    if (
                        !isStepEnabled(
                            propertySteps,
                            this._draftState.draftData.currentStepId
                        )
                    ) {
                        return StepIds.propertyInformation;
                    } else {
                        return (
                            this._draftState.draftData.currentStepId ||
                            StepIds.propertyInformation
                        );
                    }
                } else {
                    return currentStepId;
                }
            }
        }
    }
}
